// Cache identities for the `./connected-apps` door, moved here at ARCFE-1087
// with the three hooks that were free of app tethers once D1 severed transport.
//
// THE STRING LITERALS ARE FROZEN AND ARE NOT NEW; `queryKeys.freeze.test.ts`
// pins the full tuples. Note `INTEGRATION_CONTACT_SALES_REQUEST`, whose VALUE is
// the SCREAMING_SNAKE constant name itself rather than the UpperCamel spelling
// its two neighbours use — transcribed, not normalised.
//
// `GET_INTEGRATION_ONBOARDING_SESSION_BY_ID` and `GET_INTEGRATION_CAPACITIES`
// were module-private in the app hooks; a `/contracts` key factory is the
// sanctioned single declaration site, so they are exported here.
// `GET_INTEGRATION_CAPACITIES` keeps its typo-ish name ("capacities" for a
// capabilities endpoint) because the constant is imported by name elsewhere in
// this door's future work and renaming is not this PR's to do.
import { OnboardingSessionStatus } from '@heidi/schema/ml-scribe';

import type {
  GetIntegrationOnboardingSessionsQueryParams,
  IntegrationClientMode,
} from './types';

/** `GET /integrations/onboardings/sessions/{id}`. */
export const GET_INTEGRATION_ONBOARDING_SESSION_BY_ID =
  'GetIntegrationOnboardingSessionById';
/** `GET /integrations/capabilities/{provider}`. */
export const GET_INTEGRATION_CAPACITIES = 'GetIntegrationCapabilities';
/** `GET /integrations/integration-request/contact-sales`. */
export const INTEGRATION_CONTACT_SALES_REQUEST = 'INTEGRATION_CONTACT_SALES_REQUEST';

// `GET /integrations/onboardings/sessions` — the LIST identity, and the one
// declaration on this door that arrived by force rather than by preference.
//
// It was deliberately left app-side when the read hooks carved, with the reason
// recorded on the `query` dimension: nothing behind the door needed it, and
// moving a live key that a dozen app modules read buys a re-key for no gate's
// benefit. That reason held until the SECOND half of this identity had to be
// single-sited. `getCompletedSessionsQueryKey` was declared VERBATIM in both
// Athena editor predicates — the same head, the same `{ status: completed }`
// filter, one of the two files admitting the copy in its own comment — and a
// duplicated cache identity is a live bug at any age. It cannot be healed
// app-side, because a shared app declaration site is itself a new declaration
// and `@heidi/rules/no-new-cache-key-declaration` rejects one anywhere under
// `src/**`; and it cannot be healed by declaring the head twice, which is
// exactly what `key-cross-site-identity` fires on. So the head moves, the app
// path re-exports it name-intact for its six production importers and six
// `jest.mock` literals, and `cache-key-baseline.json` shrinks by three entries.
//
// A SIDE EFFECT WORTH NAMING, because it removes a workaround rather than
// adding one: the app-side factory had to be built lazily inside its function
// ("reading `GET_INTEGRATION_ONBOARDING_SESSIONS` at module-load time can hit a
// TDZ error through the existing import cycle between this module,
// `useEnableBlockEditorV1`, and `scribeAPIV2`"). This module imports nothing
// but the generated schema and its own types, so it is in no cycle and the
// hazard is gone. The factory stays a FUNCTION anyway — that is the shape
// `/contracts` key factories take, and callers are unchanged.
/** `GET /integrations/onboardings/sessions`. */
export const GET_INTEGRATION_ONBOARDING_SESSIONS = 'GetIntegrationOnboardingSessions';

/**
 * The `{ status: completed }` filter both Athena editor predicates address.
 *
 * Part of the cache identity, not a convenience: it is the second tuple element,
 * so a caller that spells the filter itself addresses a different cache entry.
 */
export const COMPLETED_ONBOARDING_SESSIONS_PARAMS = {
  status: OnboardingSessionStatus.completed,
} as const;

/** TanStack query keys for the `./connected-apps` door's carved read hooks. */
export const connectedAppsQueryKeys = {
  onboardingSessionById: (onboardingSessionId: string) =>
    [GET_INTEGRATION_ONBOARDING_SESSION_BY_ID, { onboardingSessionId }] as const,
  integrationCapabilities: (
    provider: string | null | undefined,
    mode?: IntegrationClientMode
  ) => [GET_INTEGRATION_CAPACITIES, { provider, mode }] as const,
  contactSalesRequest: (integrationName: string) =>
    [INTEGRATION_CONTACT_SALES_REQUEST, { integrationName }] as const,
  onboardingSessions: (params: GetIntegrationOnboardingSessionsQueryParams) =>
    [GET_INTEGRATION_ONBOARDING_SESSIONS, { params }] as const,
  nangoToolPermissions: (providerConfigKey: string) =>
    [NANGO_TOOL_PERMISSIONS_QUERY_KEY, { providerConfigKey }] as const,
  completedOnboardingSessions: () =>
    [
      GET_INTEGRATION_ONBOARDING_SESSIONS,
      { params: COMPLETED_ONBOARDING_SESSIONS_PARAMS },
    ] as const,
};

// ---------------------------------------------------------------------------
// Mutation identities
// ---------------------------------------------------------------------------
// Both were MODULE-PRIVATE `const`s in the app hooks that carried them, so
// nothing imported either name and no re-export is owed at the old paths. Their
// VALUES are frozen all the same: `mutationKey` participates in
// `isMutating`/`getMutationCache` lookups, and the app's `MutationCache`
// subscribers see every mutation in the app.
//
// Note the spelling asymmetry, transcribed rather than normalised — the same
// asymmetry `INTEGRATION_CONTACT_SALES_REQUEST` shows above. One value is the
// SCREAMING_SNAKE constant name itself, the other is UpperCamel.

/** `POST /integrations/integration-request`. */
export const REQUEST_EHR_INTEGRATION = 'REQUEST_EHR_INTEGRATION';
/** `POST /integrations/third-party-auth`. */
export const INTEGRATION_AUTH_TOKEN = 'IntegrationAuthToken';

// The two ONBOARDING-SESSION write heads. Module-private `const`s in the app
// hooks that carried them, exactly like the two above, so nothing imported
// either name and no re-export is owed at the old paths — the delegates there
// declare no key at all. `cache-key-baseline.json` shrinks by those two file
// entries.
//
// Their VALUES are the UpperCamel spelling the app used, transcribed rather
// than normalised onto the SCREAMING_SNAKE neighbour above. Both tuples are
// pinned BARE — one element, no argument slot — in `queryKeys.freeze.test.ts`,
// so a later widening into `[head, { … }]` cannot move either mutation out of
// the `isMutating` / `getMutationCache` lookups that match it today.

/** `POST /integrations/onboardings/sessions`. */
export const CREATE_INTEGRATION_ONBOARDING_SESSION = 'CreateIntegrationOnboardingSession';
/** `DELETE /integrations/onboardings/sessions/{id}`. */
export const DELETE_INTEGRATION_ONBOARDING_SESSION = 'DeleteIntegrationOnboardingSession';
/**
 * `PUT /integrations/onboardings/sessions/{id}/step_executions/{step_id}`.
 * The second tuple element is the caller's scope, so two actions on one card
 * do not share an `isPending`.
 */
export const UPDATE_INTEGRATION_ONBOARDING_SESSION_STEP_FORM =
  'UpdateIntegrationOnboardingSessionStepForm';

/** TanStack mutation keys for the `./connected-apps` door's carved writes. */
export const connectedAppsMutationKeys = {
  requestEHRIntegration: () => [REQUEST_EHR_INTEGRATION] as const,
  integrationAuthToken: () => [INTEGRATION_AUTH_TOKEN] as const,
  createOnboardingSession: () => [CREATE_INTEGRATION_ONBOARDING_SESSION] as const,
  deleteOnboardingSession: () => [DELETE_INTEGRATION_ONBOARDING_SESSION] as const,
  updateOnboardingSessionStep: (mutationScope: string) =>
    [UPDATE_INTEGRATION_ONBOARDING_SESSION_STEP_FORM, mutationScope] as const,
};

// ---------------------------------------------------------------------------
// Nango connection list identity
// ---------------------------------------------------------------------------
// The head is read bare — `[HEAD]` — by the connections query and its
// invalidations, so a later widening into `[head, { … }]` would orphan them.
//
// APPENDED AT THE END of this file deliberately: two non-programme PRs are
// editing the blocks above.

/** `GET /integrations/nango/connections`. */
export const NANGO_CONNECTIONS_QUERY_KEY = 'NangoConnections';
/** `GET /integrations/nango/tool-permissions/{provider_config_key}`. */
export const NANGO_TOOL_PERMISSIONS_QUERY_KEY = 'NangoToolPermissions';
/** `GET /work-chat-connections`. */
export const WORK_CHAT_CONNECTIONS_QUERY_KEY = 'WorkChatConnections';

/** `GET /integration-agent/agents/status`. */
export const INTEGRATION_AGENT_STATUS_QUERY_KEY = [
  'integration-agent',
  'status',
] as const;
